import React from 'react';
import { useLocalSearchParams, usePathname } from 'expo-router';

import { useTrackEffect } from '@order/Telemetry';

import { LoadingAnimation, NotFoundView } from '../../components';
import { useMenuRedirect, usePermanentRedirects } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const NotFoundScreen = () => {
  const routeParams = useLocalSearchParams<RouteParams>();
  const pathname = usePathname();

  // ─────────────────────────────────────────────────────────────────────

  const restaurantSlug = routeParams.restaurantSlug ?? extractRestaurantSlug(pathname); // prettier-ignore

  // ─── Handlers ────────────────────────────────────────────────────────

  const hasPermanentRedirect = usePermanentRedirects();

  const isLoadingMenuRedirect = useMenuRedirect(hasPermanentRedirect, {
    restaurantSlug,
  });

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEffect('app.not_found', { path: routeParams.path ?? pathname });

  // ─────────────────────────────────────────────────────────────────────

  if (isLoadingMenuRedirect || hasPermanentRedirect) {
    return <LoadingAnimation />;
  }

  return <NotFoundView />;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Extracts a potential restaurant slug from a single-segment pathname.
 * e.g. "/west-3rd" → "west-3rd", "/foo/bar" → undefined
 */
function extractRestaurantSlug(pathname: string) {
  if (!pathname) return;

  const segments = pathname.replace(/^\//, '').split('/');

  if (segments.length !== 1 || segments[0] === '') return;

  return segments[0];
}

// ─── Types ───────────────────────────────────────────────────────────────────

type RouteParams = {
  restaurantSlug?: string;
  path?: string;
};
